import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { StatusPill } from '@dynamika-shared/ui/StatusPill/StatusPill';
import { Link } from 'react-router-dom';
import { Button } from '@dynamika-shared/ui/Button/Button';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { QCListTableItem } from '@dynamika-shared/types/qc';
import { format } from 'date-fns';
import { Tooltip } from 'antd';
import { d7Format } from '@dynamika-shared/utils/date';
import { routesPath } from '@router/routes';
import { prepareUrl } from '@utils/routerHelpers';
import { formatStatusLabel } from '@utils/statusLabel';

const loading = () => (
  <div className='h-2 w-10 animate-pulse rounded-md bg-slate-300' />
);

export const qcListColumns = (
  clinicalTrialId: string
): ColumnType<QCListTableItem>[] => [
  {
    name: 'siteCode',
    header() {
      return 'Site ID';
    },
    getValue(row) {
      return row.siteCode ?? '-';
    },
    getCsvValue(row) {
      return row.siteCode ?? '-';
    },
    loading,
    hasSorting: true,
  },
  {
    name: 'patientCode',
    header() {
      return 'Subject ID';
    },
    getValue(row) {
      return row?.patientCode ?? '-';
    },
    getCsvValue(row) {
      return row?.patientCode ?? '-';
    },
    loading,
    hasSorting: true,
  },
  {
    name: 'visitName',
    header() {
      return 'Visit ID';
    },
    getValue(row) {
      const isUnscheduled =
        row.visitType === 'UNSCHEDULED' && !row.visitEarlyTermination;

      return isUnscheduled ? (
        <div>
          <div>Unscheduled</div>
          <div>
            {row?.visitName
              ? d7Format(row.visitName, 'dd-MMM-yyy')
              : d7Format(new Date(), 'dd-MMM-yyy')}
          </div>
        </div>
      ) : (
        row.visitName ?? '-'
      );
    },
    getCsvValue(row) {
      const isUnscheduled =
        row.visitType === 'UNSCHEDULED' && !row.visitEarlyTermination;

      if (isUnscheduled) {
        const date = row?.visitName
          ? d7Format(row.visitName, 'dd-MMM-yyy')
          : d7Format(new Date(), 'dd-MMM-yyy');
        return `Unscheduled ${date}`;
      }
      return row.visitName ?? '-';
    },
    loading,
    hasSorting: true,
  },
  {
    name: 'latestUpload',
    thClassName: 'w-[15%] min-w-[15%] max-w-[15%]',
    header() {
      return 'Latest Upload Date';
    },
    getValue(row) {
      return row.latestUpload
        ? format(new Date(row.latestUpload), 'dd-MMM-yyy')
        : '-';
    },
    getCsvValue(row) {
      return row.latestUpload
        ? format(new Date(row.latestUpload), 'dd-MMM-yyy')
        : '-';
    },
    loading,
    hasSorting: true,
  },
  {
    name: 'status',
    header() {
      return 'QC Status';
    },
    getValue(row) {
      return row.status ? (
        <StatusPill
          status={row.status}
          textSeparator='_'
          textCombiner=' '
        />
      ) : (
        '-'
      );
    },
    getCsvValue(row) {
      return formatStatusLabel(row.status, '_', ' ');
    },
    loading,
    hasSorting: true,
  },
  {
    name: 'completionDate',
    thClassName: 'w-[15%] min-w-[15%] max-w-[15%]',
    header() {
      return 'QC Completion Date';
    },
    getValue(row) {
      return row.completionDate
        ? d7Format(row.completionDate, 'dd-MMM-yyy')
        : '-';
    },
    getCsvValue(row) {
      return row.completionDate
        ? d7Format(row.completionDate, 'dd-MMM-yyy')
        : '-';
    },
    loading,
    hasSorting: true,
  },
  {
    name: 'assigneeName',
    header() {
      return 'Imaging Specialist';
    },
    getValue(row) {
      return row.assigneeName ? (
        <Tooltip
          color='white'
          title={
            <span className='text-1424 text-black'>{row.assigneeName}</span>
          }
        >
          <div className='max-w-full truncate bg-transparent'>
            {row.assigneeName}
          </div>
        </Tooltip>
      ) : (
        '-'
      );
    },
    getCsvValue(row) {
      return row.assigneeName ?? '-';
    },
    loading,
    hasSorting: false,
  },
  {
    name: 'action',
    header() {
      return 'Action';
    },
    getValue(row) {
      return (
        <Link to={prepareUrl(routesPath.qcTaskInfo, [clinicalTrialId, row.id])}>
          <Button
            type='text'
            onClick={() => null}
            className='flex h-4 !min-h-fit w-4 cursor-pointer items-center justify-center !p-0'
          >
            <ChevronIcon dir='right' className='h-2 w-2 text-pastelBlue' />
          </Button>
        </Link>
      );
    },
    loading,
  },
];
